
<template>
    <div v-if="model">
        <rkm-table
            :busy="busy"
            :fields="fields"
            :filter="filter"
            :items="filteredItems"
            ref="RkmTable"
        >
            <template v-slot:filters>
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-date-picker
                            :max-date="additionals.dataAtual"
                            label="Data"
                            v-model="model.data"
                        />
                    </rkm-col>

                    <rkm-col cols="7">
                        <rkm-form-input
                            label="Buscar"
                            placeholder="Digite para buscar"
                            v-model="filter"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.temposEspera"
                            default-option-text="Todos"
                            label="Tempo de espera"
                            show-default-option
                            text-field="minutos"
                            v-model="model.tempoEsperaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            :options="form.opcoesEntrada"
                            default-option-text="Todas"
                            label="Opção de entrada"
                            show-default-option
                            text-field="nome"
                            v-model="model.opcaoEntradaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.prioridades"
                            default-option-text="Todas"
                            label="Prioridade"
                            show-default-option
                            text-field="nome"
                            v-model="model.prioridadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.status"
                            default-option-text="Todos"
                            label="Status"
                            show-default-option
                            text-field="nome"
                            v-model="model.statusId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.ordenacoes"
                            label="Ordenação"
                            v-model="model.ordenacao"
                        />
                    </rkm-col>
                </rkm-row>
            </template>

            <template v-slot:cell(tempoEspera)="{item}">
                <rkm-icon
                    v-if="item.tempoEspera.id"
                    :id="uniqueId('tempo-espera-tooltip')"
                    :style="'color: ' + item.tempoEspera.cor"
                    :title="formatTempoEspera(item.tempoEspera.total)"
                    font-scale="2"
                    icon="battery-full"
                    v-rkm-tooltip.hover
                />

                <span
                    v-if="item.isEncaminhado"
                    :title="`Encaminhado por: ${item.responsavelEncaminhamentoAtualNome}`"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon
                        :class="{'ml-3': !!item.tempoEspera.id}"
                        icon="undo-alt"
                    />
                </span>

                <span v-if="!item.tempoEspera.id && !item.isEncaminhado">
                    -
                </span>
            </template>

            <template v-slot:cell(pacienteNome)="{index, item}">
                <template v-if="item.pacienteNome">
                    <rkm-button
                        :id="uniqueId(`paciente-icon-${index}`)"
                        class="mr-2"
                        small
                    >
                        <rkm-icon icon="user" />
                    </rkm-button>

                    <rkm-popover
                        :target="uniqueId(`paciente-icon-${index}`)"
                        placement="top"
                        title="Dados cadastrais"
                        triggers="hover"
                    >
                        <div>
                            <span>
                                <strong>Data de nascimento:</strong> {{item.pacienteDataNascimento}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>Nome da mãe:</strong> {{item.pacienteNomeMae}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CPF:</strong> {{item.pacienteCpf}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CNS:</strong> {{item.pacienteCns}}
                            </span>
                        </div>
                    </rkm-popover>

                    <template>
                        {{item.pacienteNome}}

                        <template v-if="item.pacientePreferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>
                                ({{item.pacienteNomeSocial}})
                            </strong>
                        </template>
                    </template>

                    <rkm-icon
                        v-if="!item.dataHoraAgendamento"
                        icon="door-open"
                    />

                    <rkm-icon
                        v-if="!!getPrioridadeIcon(item)"
                        :icon="getPrioridadeIcon(item)"
                    />
                </template>
            </template>

            <template v-slot:cell(servicos)="{item}">
                {{ item.servicos.map(s => s.nome).join(', ') }}
            </template>

            <template v-slot:cell(actions)="{item}">
                <template v-if="form.telas.length > 0">
                    <rkm-dropdown
                        v-if="form.telas.length > 1"
                        :color="item.chamadas > 0 ? 'secondary' : 'info'"
                        class="mr-2"
                        small
                        title="Chamar"
                        v-rkm-tooltip.hover
                    >
                        <template v-slot:button-content>
                            <rkm-icon icon="volume-up" />

                            <rkm-badge v-if="item.chamadas > 0">
                                {{ item.chamadas }}
                            </rkm-badge>
                        </template>

                        <rkm-dropdown-item
                            v-for="tela in form.telas"
                            @click="call(item.id, tela.id)"
                            v-bind:key="tela.id"
                        >
                            {{tela.nome}}
                        </rkm-dropdown-item>
                    </rkm-dropdown>

                    <rkm-button
                        v-else
                        :color="item.chamadas > 0 ? 'secondary' : 'info'"
                        @click="call(item.id, form.telas[0].id)"
                        small
                        title="Chamar"
                    >
                        <rkm-icon icon="volume-up" />

                        <rkm-badge v-if="item.chamadas > 0">
                            {{item.chamadas}}
                        </rkm-badge>
                    </rkm-button>
                </template>

                <rkm-button
                    v-if="item.statusId === enums.status.EM_ESPERA"
                    @click="$refs.ModalEncaminhamento.open(item)"
                    color="info"
                    small
                    title="Encaminhar para outro setor"
                >
                    <rkm-icon icon="share" />
                </rkm-button>

                <rkm-button
                    @click="create(item)"
                    color="primary"
                    small
                    title="Atender"
                >
                    <rkm-icon icon="plus" />
                </rkm-button>

                <rkm-button
                    @click="$refs.ModalRegistroEvasao.open(item.id)"
                    color="danger"
                    small
                    title="Evadir"
                >
                    <rkm-icon icon="running" />
                </rkm-button>
            </template>
        </rkm-table>

        <ModalChamada
            :salas="form.salas"
            ref="ModalChamada"
        />

        <ModalRegistroEvasao ref="ModalRegistroEvasao" />

        <ModalEncaminhamento
            :data="{form}"
            ref="ModalEncaminhamento"
        />
    </div>
</template>

<script>
// Components.
import ModalChamada from '@/components/ambulatorio/components/chamada/Modal'
import ModalEncaminhamento from '@/components/ambulatorio/atendimento/components/encaminhamento/Modal'
import ModalRegistroEvasao from '@/components/ambulatorio/components/fila/components/evasao/components/registro/Modal'

// Enums.
import modulo from '@/enums/administracao/modulo'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import prioridadeTipo from '@/enums/painel-senha/prioridadeTipo'
import setorTipo from '@/enums/cadastro/estabelecimento/setorTipo'
import status from '@/enums/painel-senha/status'

// Mixins.
import {error, repository, stringCase, websocket} from '@/mixins'

// Vuex.
import {mapActions, mapGetters} from 'vuex'

// Moment.
import moment from 'moment'

export default {
    components: {
        ModalChamada,
        ModalEncaminhamento,
        ModalRegistroEvasao,
    },
    computed: {
        ...mapGetters({
            centroCusto: 'getCentroCusto',
            customer: 'getCustomer',
            lotacao: 'getLotacao',
        }),
        filteredItems() {
            let fila = this.model.fila

            if (this.model.data) {
                fila = fila.filter(item => this.others.moment(item.emitidoEm, 'DD/MM/YYYY').isSame(this.model.data))
            }

            if (this.model.tempoEsperaId) {
                fila = fila.filter(item => item.tempoEspera.id === this.model.tempoEsperaId)
            }

            if (this.model.opcaoEntradaId) {
                fila = fila.filter(item => item.opcaoEntradaId === this.model.opcaoEntradaId)
            }

            if (this.model.prioridadeId) {
                fila = fila.filter(item => item.prioridadeId === this.model.prioridadeId)
            }

            if (this.model.statusId) {
                fila = fila.filter(item => item.statusId === this.model.statusId)
            }

            return fila.sort((a, b) => {

                let sort = this.sortNumber(a.classificacaoRiscoId, b.classificacaoRiscoId, false)

                if (this.model.ordenacao === 'dataHoraAgendamento') {

                    sort = sort
                        || this.sortString(a.dataHoraAgendamento, b.dataHoraAgendamento)
                        || this.sortNumber(a.prioridadeGrau, b.prioridadeGrau, false)
                }
                else if (this.model.ordenacao === 'dataHoraChegada') {

                    sort = sort
                        || this.sortNumber(a.prioridadeGrau, b.prioridadeGrau, false)
                        || this.sortString(a.dataHoraChegada, b.dataHoraChegada)
                }

                return sort || this.sortNumber(a.numeroSenha, b.numeroSenha)
            })
        },
    },
    created() {
        this.repositories = {
            chamada: this.getConectaSusRepository('ambulatorio.chamada'),
            fila: this.getConectaSusRepository('ambulatorio.fila'),
        }
    },
    data: () => ({
        additionals: null,
        busy: false,
        enums: {
            modulo,
            preferenciaNome,
            prioridadeTipo,
            setorTipo,
            status,
        },
        fields: [
            {key: 'tempoEspera', label: 'Tempo de espera'},
            {key: 'emitidoEm', label: 'Data'},
            {key: 'opcaoEntradaNome', label: 'Opção de entrada'},
            {key: 'senha', label: 'Senha', name: 'senha'},
            {key: 'prioridadeNome', label: 'Prioridade', name: 'prioridadeNome'},
            {key: 'horarioChegada', label: 'Horário de chegada', name: 'horarioChegada'},
            {key: 'pacienteNome', label: 'Nome', name: 'pacienteNome'},
            {key: 'pacienteIdade', label: 'Idade', name: 'pacienteIdade'},
            {key: 'pacienteProntuario', label: 'Prontuário', name: 'pacienteProntuario'},
            {key: 'usuarioNome', label: 'Profissional', name: 'usuarioNome'},
            {key: 'horarioAgendado', label: 'Horário agendado', name: 'horarioAgendado'},
            {key: 'servicos', label: 'Serviço', name: 'servicos'},
            {class: 'col-min text-center', key: 'actions', label: 'Ações', name: 'actions'},
        ],
        filter: null,
        form: null,
        model: null,
        others: {moment},
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        sortString(a, b, ascending = true) {

            return this.sortNullsLast(a, b, () => ascending ? a.localeCompare(b) : b.localeCompare(a))
        },
        sortNumber(a, b, ascending = true) {

            return this.sortNullsLast(a, b, () => ascending ? a - b : b - a)
        },
        sortNullsLast(a, b, sort) {

            if (a === b) {

                return 0
            }
            else if (a === null) {

                return 1
            }
            else if (b === null) {

                return -1
            }
            else {

                return sort()
            }
        },
        init() {
            this.pusher.subscribe(`private-${this.customer.link}-painel-senha-fila`)
                .bind(`${this.centroCusto.setor.tipoId}.${this.centroCusto.id}.${this.enums.modulo.AMBULATORIO}.updated`, (data) => {
                    this.model.fila = Object.assign(this.snakeObjectKeysToCamelRecursive(data.fila))
                })
        },
        load() {
            this.setLoading()

            const request = {
                centroCustoId: this.centroCusto.id,
                setorTipoId: this.enums.setorTipo.SAUDE_BUCAL,
            }

            this.repositories.fila.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    response.data.model.data = this.others.moment(response.data.model.data, 'DD/MM/YYYY').toDate()

                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)

                    this.$emit('data-load', { additionals: this.additionals })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        call(filaId, telaId) {

            const request = {
                filaId: filaId,
                telaId: telaId,
            }

            if (this.form.salas.length > 0) {
                this.$refs.ModalChamada.open(filaId, telaId)
            } else {
                this.busy = true

                this.repositories.chamada.store(this.camelObjectKeysToSnakeRecursive(request))
                    .catch(this.handleError)
                    .finally(() => this.busy = false)
            }
        },
        create(item) {

            if (item.enderecoDesatualizadoBloqueado) {

                this.showAlert(
                    'O endereço do paciente ainda não foi atualizado pela Base Nacional. Atualize o cadastro para continuar.',
                    'Atenção'
                )

                return
            }

            if (item.diasCadastroDesatualizado > 0) {
                const confirm = async () => {
                    this.$router.push({name: 'acolhimento-saude-bucal-create', params: {id: item.id}})
                }

                let msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`

                if (!item.isOrigemBaseNacional) {

                    msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também deve ser atualizado. Deseja continuar?`
                }

                this.showConfirmation(msg, confirm)
            } else {
                this.$router.push({name: 'acolhimento-saude-bucal-create', params: {id: item.id}})
            }
        },
        formatTempoEspera(value) {
            let format = 'Esperando por'

            if (value.dias !== null) {
                format = `${format} ${value.dias} dia(s)`
            }

            if (value.horas !== null) {
                format = `${format} ${value.horas} hora(s)`
            }

            if (value.minutos !== null) {
                if (value.horas !== null) {
                    format = `${format} e ${value.minutos} minuto(s)`
                } else {
                    format = `${format} ${value.minutos} minuto(s)`
                }
            }

            return format
        },
        getPrioridadeIcon(item) {

            switch (item.prioridadeId) {

                case this.enums.prioridadeTipo.IDOSO: {

                    return 'blind'
                }
                case this.enums.prioridadeTipo.PESSOA_COM_DEFICIENCIA: {

                    return 'wheelchair'
                }
                case this.enums.prioridadeTipo.PESSOA_COM_CRIANCA_DE_COLO: {

                    return 'baby'
                }
                default: {

                    return null
                }
            }
        }
    },
    mixins: [error, repository, stringCase, websocket],
    mounted() {
        this.init()
        this.load()
    },
}
</script>
